import { useThread, useThreadRuntime } from "@assistant-ui/react";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { choiceOption, choicesInputStyles as s } from "./ChoicesInput.styles";
import { EntityAvatar } from "./EntityAvatar";
import { useGenUIInput } from "./GenUIInputContext";
import { CHOICES_PAGE_SIZE, type ChoiceOption, type ChoicesData } from "./ChoicesInput.types";

/** Message sent when the user dismisses the prompt without choosing. */
const SKIP_MESSAGE = "Skip this question.";

/**
 * The text sent for a picked option. Look-alike options (e.g. several
 * conversations with the same person) differ only in their description, so it
 * rides along to keep the answer unambiguous.
 */
function answerText(opt: ChoiceOption): string {
  return opt.description ? `${opt.label} (${opt.description})` : opt.label;
}

export function ChoicesInput({ prompt, options, select_mode = "single" }: ChoicesData) {
  const threadRuntime = useThreadRuntime();
  const { dismissInput } = useGenUIInput();
  const isRunning = useThread((t) => t.isRunning);
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [freeText, setFreeText] = useState("");

  // Every exit path continues the conversation with a fresh /invocations turn.
  const answer = (text: string) => {
    threadRuntime.append(text);
    dismissInput();
  };
  const submitLabels = (labels: string[]) => {
    if (labels.length > 0) answer(labels.join(", "));
  };
  const submitFreeText = () => {
    const text = freeText.trim();
    if (text) answer(text);
  };

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const isMulti = select_mode === "multi";

  // At most CHOICES_PAGE_SIZE options are visible at once; longer lists page
  // in groups of that size. Multi-select picks persist across pages.
  const pageCount = Math.max(1, Math.ceil(options.length / CHOICES_PAGE_SIZE));
  const visible = options.slice(page * CHOICES_PAGE_SIZE, (page + 1) * CHOICES_PAGE_SIZE);

  return (
    <div className={s.root}>
      <div className={s.header}>
        <p className={s.prompt}>{prompt}</p>
        <button
          onClick={() => answer(SKIP_MESSAGE)}
          disabled={isRunning}
          aria-label="Dismiss"
          title="Dismiss"
          className={s.dismissBtn}
        >
          <X size={14} />
        </button>
      </div>

      <div className={s.options}>
        {visible.map((opt) => {
          const isSelected = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              // Single-select submits immediately; multi-select toggles until Send.
              onClick={() => (isMulti ? toggle(opt.id) : submitLabels([answerText(opt)]))}
              disabled={isRunning}
              className={choiceOption({ selected: isMulti && isSelected })}
            >
              {isMulti &&
                (isSelected ? (
                  <Check className={s.checkIcon} />
                ) : (
                  <span className={s.checkPlaceholder} />
                ))}
              {opt.entity_type && <EntityAvatar entityType={opt.entity_type} title={opt.label} />}
              <span className={s.optionText}>
                <span className={s.optionLabel}>{opt.label}</span>
                {opt.description && <span className={s.optionDescription}>{opt.description}</span>}
              </span>
            </button>
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className={s.pager}>
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
            aria-label="Previous options"
            className={s.pagerBtn}
          >
            <ChevronLeft size={14} />
          </button>
          <span className={s.pagerLabel}>
            {page + 1} of {pageCount}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === pageCount - 1}
            aria-label="More options"
            className={s.pagerBtn}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {isMulti && (
        <button
          onClick={() =>
            submitLabels(options.filter((o) => selected.includes(o.id)).map(answerText))
          }
          disabled={selected.length === 0 || isRunning}
          className={s.submitBtn}
        >
          Send
        </button>
      )}

      {/* Always available, on every page: answer in your own words instead. */}
      <form
        className={s.freeTextRow}
        onSubmit={(e) => {
          e.preventDefault();
          submitFreeText();
        }}
      >
        <input
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          disabled={isRunning}
          placeholder="Or type your own answer"
          aria-label="Type your own answer"
          className={s.freeTextInput}
        />
        <button
          type="submit"
          disabled={!freeText.trim() || isRunning}
          aria-label="Send your own answer"
          className={s.freeTextSend}
        >
          <ChevronRight size={14} />
        </button>
      </form>
    </div>
  );
}
